import { useState } from 'react';
import { Library } from 'lucide-react';
import { Engineer } from '@/features/normas/Engineer';
import { NormCard } from '@/features/normas/NormCard';
import { NormDetailModal } from '@/features/normas/NormDetailModal';
import { NORM_GROUPS, type NormEntry } from '@/features/normas/catalog';

export function NormasPage() {
  const [open, setOpen] = useState<NormEntry | null>(null);

  return (
    <div className="min-h-full" style={{ background: 'var(--surface-base)' }}>
      <Hero />

      <div className="mx-auto max-w-7xl px-8 pb-16">
        {NORM_GROUPS.map((group) => (
          <section key={group.id} className="mt-12">
            <header className="mb-5">
              <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--accent)' }}>
                {group.id === 'norma-peru'
                  ? 'Reglamento Nacional de Edificaciones'
                  : group.id === 'codigo-internacional'
                    ? 'Codigos internacionales'
                    : 'Bibliografia'}
              </div>
              <h2
                className="mt-1 text-2xl"
                style={{ fontFamily: 'var(--font-display)', fontWeight: 700, letterSpacing: '-0.02em' }}
              >
                {group.title}
              </h2>
              <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                {group.subtitle}
              </p>
            </header>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {group.entries.map((n) => (
                <NormCard key={n.id} norm={n} onOpen={setOpen} />
              ))}
            </div>
          </section>
        ))}
      </div>

      <NormDetailModal norm={open} onClose={() => setOpen(null)} />
    </div>
  );
}

function Hero() {
  return (
    <section
      className="relative overflow-hidden border-b"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            'linear-gradient(var(--text-muted) 1px, transparent 1px), linear-gradient(to right, var(--text-muted) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          maskImage: 'radial-gradient(ellipse at 30% 50%, black 35%, transparent 85%)',
          opacity: 0.08,
        }}
      />
      <div className="mx-auto flex max-w-7xl flex-col items-center gap-10 px-8 py-12 md:flex-row">
        <div className="flex-1">
          <div className="mb-3 inline-flex items-center gap-2 rounded-[var(--radius-pill)] border px-3 py-1 text-xs"
            style={{
              borderColor: 'var(--border-default)',
              background: 'var(--surface-2)',
              color: 'var(--text-secondary)',
            }}>
            <Library size={12} strokeWidth={1.8} style={{ color: 'var(--accent)' }} />
            Normas y referencias
          </div>
          <h1
            className="text-3xl md:text-4xl"
            style={{ fontFamily: 'var(--font-display)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1 }}
          >
            Tu ingeniero estructural
            <br />
            <span style={{ color: 'var(--accent)' }}>te explica las normas.</span>
          </h1>
          <p className="mt-4 max-w-xl text-sm md:text-base" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Cada calculo en AlphaConcreto se hace contra una norma. Aqui puedes ver cuales normas se aplican,
            que dicen y donde exactamente las usa el motor de la app. Sin tecnicismos innecesarios.
          </p>
        </div>
        <div className="shrink-0">
          <Engineer size={220} />
        </div>
      </div>
    </section>
  );
}
